import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { search, searchKeymap } from "@codemirror/search";
import { drawSelection, dropCursor, EditorView, highlightSpecialChars, keymap, placeholder } from "@codemirror/view";
import { Compartment, EditorState, type Extension } from "@codemirror/state";
import { indentOnInput, indentUnit } from "@codemirror/language";
import { yCollab, yUndoManagerKeymap } from "y-codemirror.next";
import type * as Y from "yjs";
import { editorTheme } from "./theme";
import { hydraSyntaxHighlighting } from "./highlight";
import { hydraMarkdownExtensions } from "./markdown-extensions";
import { livePreview, editorCallbacks, type EditorCallbacks } from "./live-preview";
import { markdownKeymap } from "./commands";
import { attachmentUploads } from "./attachments";
import { hydraCompletions } from "./wikilink-complete";
import { codeBlockExtras } from "./code-blocks";
import { selectionToolbar } from "./selection-toolbar";
import { smartPaste } from "./smart-paste";
import { callouts } from "./callouts";
import { focusModeHighlight } from "./focus-mode";
import { selectionStatsReporter } from "./selection-stats";

export interface YjsBinding {
  text: Y.Text;
  undoManager: Y.UndoManager;
  /** Awareness instance once collaboration/presence is wired; null offline. */
  awareness?: unknown;
}

/**
 * Read-only switch, reconfigurable without rebuilding the view. Used for
 * viewer-role shared notes and for shared notes while disconnected (they are
 * online-only, so an offline edit would have nowhere to go).
 */
const readOnlyCompartment = new Compartment();

const readOnlyExt = (v: boolean): Extension => [EditorState.readOnly.of(v), EditorView.editable.of(!v)];

export function setEditorReadOnly(view: EditorView, readOnly: boolean): void {
  view.dispatch({ effects: readOnlyCompartment.reconfigure(readOnlyExt(readOnly)) });
}

/**
 * The full extension stack for a Hydra Notes editor. When a Yjs binding is
 * given, the Y.UndoManager replaces CodeMirror history (they must never
 * coexist — double-undo corruption).
 */
export function hydraEditorExtensions(opts: {
  callbacks?: Partial<EditorCallbacks>;
  extra?: Extension[];
  yjs?: YjsBinding;
  readOnly?: boolean;
}): Extension[] {
  return [
    readOnlyCompartment.of(readOnlyExt(opts.readOnly ?? false)),
    // language
    markdown({
      base: markdownLanguage,
      codeLanguages: languages,
      extensions: hydraMarkdownExtensions,
    }),

    // document binding + undo
    opts.yjs
      ? [
          yCollab(opts.yjs.text, (opts.yjs.awareness as never) ?? null, { undoManager: opts.yjs.undoManager }),
          keymap.of(yUndoManagerKeymap),
        ]
      : history(),
    drawSelection(),
    dropCursor(),
    indentOnInput(),
    indentUnit.of("  "),
    EditorState.tabSize.of(2),
    EditorView.lineWrapping,
    highlightSpecialChars(),
    placeholder("Start writing…"),

    // look
    editorTheme,
    hydraSyntaxHighlighting,
    livePreview,
    callouts,
    codeBlockExtras,
    selectionToolbar,
    attachmentUploads,
    smartPaste,
    focusModeHighlight,
    selectionStatsReporter,
    hydraCompletions,
    editorCallbacks.of(opts.callbacks ?? {}),

    // keymaps: markdown first so its bindings win where they overlap
    keymap.of([
      ...markdownKeymap,
      ...defaultKeymap,
      ...(opts.yjs ? [] : historyKeymap),
      ...searchKeymap,
      indentWithTab,
    ]),
    search({ top: true }),

    ...(opts.extra ?? []),
  ];
}
